import { Link } from "react-router-dom";
import { Clock, ArrowRight, User } from "lucide-react";
import Image from "./Image";

import { useTranslation } from 'react-i18next';
// Generate Unsplash srcset with multiple widths
function unsplashSrcset(url) {
  if (!url || !url.includes("unsplash.com")) return null;
  const base = url.split("?")[0];
  const params = "fit=crop&auto=format";
  return [
    `${base}?w=320&${params} 320w`,
    `${base}?w=640&${params} 640w`,
    `${base}?w=960&${params} 960w`,
    `${base}?w=1280&${params} 1280w`,
  ].join(", ");
}

export default function BlogCard({ post, featured = false, readingTime: readingTimeProp }) {
  const { t } = useTranslation();
  if (!post) return null;

  const srcSet = unsplashSrcset(post.ogImage);
  const sizes = featured
    ? "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 66vw"
    : "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw";

  const displayReadTime = readingTimeProp || post.readTime || "1 min read";

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group block min-h-[44px] bg-[#09090B] border border-zinc-900 hover:border-zinc-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B] ${featured ? "md:col-span-2" : ""}`}
    >
      {/* Thumbnail */}
      <div className={`relative overflow-hidden border-b border-zinc-900 ${featured ? "aspect-[16/9] sm:aspect-[21/9]" : "aspect-[16/9]"}`}>
        <Image
          src={post.ogImage}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          srcSet={srcSet}
          sizes={sizes}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="bg-black/70 backdrop-blur-sm border border-zinc-700 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-amber-500">
            {post.category || post.tag}
          </span>
          {featured && (
            <span className="bg-amber-500 text-black px-2 py-1 font-mono text-[9px] uppercase tracking-wider font-bold">{t('blog_card.featured')}</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={`p-4 sm:p-6 ${featured ? "md:p-8" : ""}`}>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            {new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </span>
          <span className="text-zinc-700" aria-hidden="true">·</span>
            <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              <Clock className="w-3 h-3" aria-hidden="true" />
              {displayReadTime}
            </span>
          {post.author && (
            <>
              <span className="text-zinc-700" aria-hidden="true">·</span>
              <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                <User className="w-3 h-3" aria-hidden="true" />
                {post.author.name}
              </span>
            </>
          )}
        </div>

        <h3 className={`font-display uppercase leading-tight text-white group-hover:text-amber-500 transition-colors mb-3 ${featured ? "text-lg sm:text-xl md:text-2xl" : "text-base sm:text-lg"}`}>
          {post.title}
        </h3>

        <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-4">
          {post.excerpt}
        </p>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {post.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 border border-zinc-800 text-[9px] font-mono uppercase tracking-wider text-zinc-500"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 group-hover:gap-3 transition-all">
          Read Article <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}
